import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Modal,
  TextField,
  useTheme,
  styled,
  Tooltip,
} from "@mui/material";
import { tokens } from "../../theme";
import secureLocalStorage from "react-secure-storage";
import { jwtDecode } from "jwt-decode";
import {
  ShowVendorCalenderEvents,
  createEventInCalender,
} from "../../../../context/Slice/CalenderSlice";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import axios from "axios";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const CustomModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .modal-box": {
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    borderRadius: 8,
  },
}));

const VendorCalendar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventData, setEventData] = useState({
    Title: "",
    Description: "",
    Start_Date: "",
    End_Date: "",
    allDay: false,
  });

  const token = secureLocalStorage.getItem("token");
  const vendorId = localStorage.getItem("userId");

  useEffect(() => {
    if (token) {
      axios
        .get(`${BASE_URL}/api/v1/VendorCalenders/get_InVendorCalender`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          console.log("API Response:", response.data);
          setEvents(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching events:", error);
        });
    }
  }, [token]);

  useEffect(() => {
    if (events) {
      const formattedEvents = events.map((event) => {
        const start = event.Start_Date
          ? moment(event.Start_Date).toISOString()
          : null;
        const end = event.End_Date
          ? moment(event.End_Date).toISOString()
          : null;
        const proposedDate = event.Proposed_Date
          ? moment(event.Proposed_Date).toISOString()
          : null;

        const baseEvent = {
          id: event.id,
          title: event.Title,
          description: event.Description || "No description", // ✅ Add Description
          extendedProps: { description: event.Description || "No description" }, // ✅ Store in extendedProps
          allDay: event.Whole_day,
        };

        if (!start && !end && proposedDate) {
          return {
            ...baseEvent,
            start: proposedDate,
            end: proposedDate,
            backgroundColor: "yellow",
          };
        } else if (start && end) {
          return {
            ...baseEvent,
            start,
            end,
          };
        } else {
          return {
            ...baseEvent,
            start: start || proposedDate,
            end: end || proposedDate,
          };
        }
      });

      setCurrentEvents(formattedEvents);
    }
  }, [events]);

  const handleDateClick = (selected) => {
    console.log("Selected date data:", selected);

    if (!selected || !selected.startStr) {
      console.error("Invalid date selection");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate comparison
    const selectedDate = new Date(selected.startStr);

    if (selectedDate < today) {
      alert("You cannot select past dates.");
      return;
    }

    setSelectedInfo(selected); // ✅ Preserve selected event info
    setEventData((prev) => ({
      ...prev,
      Start_Date: selected.startStr,
      End_Date: selected.endStr || selected.startStr, // Ensure end date is set
      allDay: selected.allDay,
    }));

    setOpen(true);
  };

  // const handleDateClick = (selected) => {
  //   setSelectedInfo(selected);
  //   setEventData({
  //     ...eventData,
  //     Start_Date: selected.startStr,
  //     End_Date: selected.endStr,
  //     allDay: selected.allDay,
  //   });
  //   setOpen(true);
  // };

  const handleEventClick = async (selected) => {
    if (
      window.confirm(
        `Are you sure you want to delete the event '${selected.event.title}'?`
      )
    ) {
      try {
        const eventId = selected.event.id;

        const response = await axios.delete(
          `${BASE_URL}/api/v1/VendorCalenders/delete_vendorCalender/${eventId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          // Remove from state
          setCurrentEvents((prevEvents) =>
            prevEvents.filter((event) => event.id !== eventId)
          );

          // Remove from UI
          selected.event.remove();
          window.location.reload();
          alert("Event deleted successfully.");
        } else {
          alert("Failed to delete the event.");
        }
      } catch (error) {
        console.error("Error deleting event:", error);
        alert("Error deleting event. Please try again.");
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value,
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const calendarApi = selectedInfo.view.calendar;
    calendarApi.unselect();

    if (eventData.Title) {
      try {
        const response = await axios.post(
          `${BASE_URL}/api/v1/VendorCalenders/createVendor-InCalender`,
          {
            vendor_id: vendorId,
            Type: "inCalender",
            Title: eventData.Title,
            Description: eventData.Description,
            Whole_day: eventData.allDay,
            Start_Date: eventData.Start_Date,
            End_Date: eventData.End_Date,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          const newEvent = {
            id:
              response.data?.id || `${eventData.Start_Date}-${eventData.Title}`, // Use API response ID if available
            title: eventData.Title,
            description: eventData.Description,
            start: eventData.Start_Date,
            end: eventData.End_Date,
            allDay: eventData.allDay,
            extendedProps: {
              description: eventData.Description,
            },
          };
          // ✅ Check if the event already exists before adding to state
          setCurrentEvents((prevEvents) => {
            const isDuplicate = prevEvents.some(
              (event) => event.id === newEvent.id
            );
            return isDuplicate ? prevEvents : [...prevEvents, newEvent];
          });

          // ✅ Ensure FullCalendar gets updated correctly
          setTimeout(() => {
            calendarApi.refetchEvents();
          }, 100);

          // ✅ Add event to FullCalendar
          // calendarApi.addEvent(newEvent);

          // ✅ Add event to state so it appears in the sidebar

          setOpen(false);
          setEventData({
            Title: "",
            Description: "",
            Start_Date: "",
            End_Date: "",
            allDay: false,
          });
        }
      } catch (error) {
        console.error("Error creating event:", error);
        alert("Failed to create event. Please try again.");
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEventData({
      Title: "",
      Description: "",
      Start_Date: "",
      End_Date: "",
      allDay: false,
    });
  };

  const renderEventContent = (eventInfo) => {
    return (
      <div
        style={{
          backgroundColor:
            eventInfo.event.extendedProps.backgroundColor || "inherit",
        }}
      >
        <b>{eventInfo.timeText}</b>
        <i>{eventInfo.event.title}</i>
      </div>
    );
  };
  const HandleBack = () => {
    console.log("Back button clicked");
    navigate("/vendor-dashboard");
  };

  return (
    <Box m="20px">
      {/* Back Button */}
      <Button
        variant="contained"
        color="secondary"
        onClick={HandleBack}
        sx={{
          position: "absolute",
          top: "20px",
          left: "20px",
          zIndex: 1000,
        }}
      >
        Back
      </Button>
      <h1 className="calendar-heading">Check your scheduled events</h1>

      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }} // Stack on small screens
        justifyContent="space-between"
        marginTop={{ xs: "60px", md: "70px" }}
        gap="20px" // Add gap for spacing
      >
        {/* CALENDAR SIDEBAR */}

        <Box
          flex="1"
          backgroundColor={colors.primary[400]}
          p="15px"
          borderRadius="4px"
          sx={{
            maxWidth: { xs: "100%", md: "20%" }, // Full width on small screens
          }}
        >
          <Typography
            variant="h5"
            sx={{ mb: 2, fontSize: { xs: "1.2rem", md: "1.5rem" } }}
          >
            Events
          </Typography>
          <List>
            {currentEvents.map((event) => {
              console.log("Rendering Event:", event); // ✅ Log each event before rendering

              return (
                <Tooltip
                  key={event.id}
                  title={
                    <div style={{ padding: "10px", maxWidth: "300px" }}>
                      <Typography variant="h6" fontWeight="bold">
                        {event.title}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Description:</strong>{" "}
                        {event.extendedProps?.description || "No description"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Start:</strong>{" "}
                        {event.start
                          ? moment(event.start).format("DD/MM/YY")
                          : "Invalid date"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>End:</strong>{" "}
                        {event.end
                          ? moment(event.end).format("DD/MM/YY")
                          : "N/A"}
                      </Typography>
                    </div>
                  }
                  arrow
                  placement="right"
                  sx={{
                    "& .MuiTooltip-tooltip": {
                      fontSize: "1rem",
                      backgroundColor: "#333",
                      color: "white",
                      padding: "12px",
                      maxWidth: "400px",
                    },
                  }}
                >
                  <ListItem
                    sx={{
                      backgroundColor: colors.greenAccent[500],
                      margin: "10px 0",
                      borderRadius: "2px",
                      padding: "10px",
                      cursor: "pointer",
                    }}
                  >
                    <ListItemText
                      primary={event.title}
                      secondary={
                        <>
                          <Typography variant="body2">
                            Start Date:{" "}
                            {event.start
                              ? moment(event.start).format("DD/MM/YY")
                              : "No start date"}
                          </Typography>
                          <Typography variant="body2">
                            End Date:{" "}
                            {event.end
                              ? moment(event.end).format("DD/MM/YY")
                              : "No end date"}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </Tooltip>
              );
            })}
          </List>
        </Box>

        {/* CALENDAR */}
        <Box
          flex="1"
          ml={{ xs: 0, md: "15px" }}
          sx={{ width: "100%", overflow: "hidden" }}
        >
          <FullCalendar
            height="75vh"
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
            }}
            initialView="dayGridMonth"
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            select={handleDateClick}
            eventClick={handleEventClick}
            events={currentEvents}
            eventContent={(arg) => (
              <Tooltip
                title={
                  <div
                    style={{
                      padding: "15px",
                      maxWidth: "400px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      backgroundColor: "#333",
                      color: "#fff",
                      borderRadius: "8px",
                    }}
                  >
                    <strong
                      style={{ fontSize: "18px", textDecoration: "underline" }}
                    >
                      {arg.event.title}
                    </strong>
                    <br />
                    {arg.event.extendedProps.Description && (
                      <>
                        <em style={{ fontSize: "16px" }}>
                          {arg.event.extendedProps.Description}
                        </em>
                        <br />
                      </>
                    )}
                    <span style={{ fontSize: "14px" }}>
                      Start: {arg.event.start.toLocaleDateString()}
                    </span>
                    <br />
                    {arg.event.end && (
                      <span style={{ fontSize: "14px" }}>
                        End: {arg.event.end.toLocaleDateString()}
                      </span>
                    )}
                  </div>
                }
                arrow
                placement="left"
                PopperProps={{
                  modifiers: [
                    {
                      name: "preventOverflow",
                      options: {
                        boundary: "window",
                      },
                    },
                  ],
                }}
              >
                <div style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  {arg.event.title}
                </div>
              </Tooltip>
            )}
          />
        </Box>
      </Box>

      {/* CUSTOM MODAL FORM */}
      <CustomModal open={open} onClose={handleClose}>
        <Box className="modal-box" sx={{ padding: "20px" }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Add New Event
          </Typography>
          <form onSubmit={handleFormSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Event Title"
              name="Title"
              value={eventData.Title}
              onChange={handleFormChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="Description"
              value={eventData.Description}
              onChange={handleFormChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Start Date"
              name="Start_Date"
              type="datetime-local"
              value={eventData.Start_Date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().slice(0, 16), // Correctly formatted for datetime-local
              }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="End Date"
              name="End_Date"
              type="datetime-local"
              value={eventData.End_Date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().slice(0, 16), // Correctly formatted for datetime-local
              }}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Add Event
            </Button>
          </form>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default VendorCalendar;
